

import { useAtom } from "jotai";
import React, { useState, useEffect, useRef, Dispatch, SetStateAction } from "react";
import { GoArrowRight, GoAlert } from "react-icons/go";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { Loader2, FileText, Shield, ClipboardCheck, X } from "lucide-react";
import i18next from "i18next";
import {
    formValuesBillingAtom,
    formValuesClubAtom,
    itemsAtom,
} from "src/atoms/constantsCheckout";
import { BASE_URL, BASE_URL_IMG } from "src/config";

// Import legal documents
// @ts-ignore
import privacyEn from "src/assets/legal/Privacy_Policy.pdf";
// @ts-ignore
import privacyEs from "src/assets/legal/Privacy_Policy_ES.pdf";
// @ts-ignore
import termsEn from "src/assets/legal/TERMINOS_CONDICIONES.pdf";
// @ts-ignore
import termsEs from "src/assets/legal/TERMINOS_CONDICIONES_ES.pdf";
// @ts-ignore
import dpaEn from "src/assets/legal/DPA.pdf";
// @ts-ignore
import dpaEs from "src/assets/legal/DPA_ES.pdf";
// @ts-ignore
import contractEn from "src/assets/legal/Contrato_Subscription.pdf";
// @ts-ignore
import contractEs from "src/assets/legal/Contrato_Subscription_ES.pdf";
import SignatureCanvas from "react-signature-canvas";

interface Props {
    step: number;
    setStep: Dispatch<SetStateAction<number>>;
}

interface ValidationErrors {
    [key: string]: string;
}

interface Country {
    country_code: string;
    name_en: string;
    country_flag: string;
    name: string;
}

const ClubCreationStep = ({ step, setStep }: Props) => {
    const [items, setItems] = useAtom(itemsAtom);
    const [countries, setCountries] = useState<Country[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [isDropdownVisible, setIsDropdownVisible] = useState(false);
    const [categoryID, setCategoryID] = useState();
    const [divisionID, setDivisionID] = useState();
    const { t } = useTranslation();
    const [nationalitieCodes, setNationalityCodes] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [countriesLoading, setCountriesLoading] = useState(true);
    const [isContractSigned, setIsContractSigned] = useState(false);
    const isEnglish = i18next.language.startsWith("en");
    const privacy = isEnglish ? privacyEn : privacyEs;
    const terms = isEnglish ? termsEn : termsEs;
    const dpa = isEnglish ? dpaEn : dpaEs;
    const contract = isEnglish ? contractEn : contractEs;

    // Legal agreements state
    const [agreements, setAgreements] = useState({
        dpa: false,
        terms: false,
        contract: false,
    });
    const [agreementErrors, setAgreementErrors] = useState<ValidationErrors>({});
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalContent, setModalContent] = useState("");
    const signatureRef = useRef<any>(null);

    // Validation states
    const [clubErrors, setClubErrors] = useState<ValidationErrors>({});
    const [billingErrors, setBillingErrors] = useState<ValidationErrors>({});
    const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>(
        {}
    );

    const [formValuesClub, setFormValuesClub] = useAtom(formValuesClubAtom);
    const [formValuesBilling, setFormValuesBilling] = useAtom(
        formValuesBillingAtom
    );

    const clubSchema = z.object({
        clubName: z
            .string()
            .min(2, `${t("checkout.validationSchema.clubName.min")}`)
            .max(100, `${t("checkout.validationSchema.clubName.max")}`)
            .regex(
                /^[a-zA-Z0-9\s\-_.]+$/,
                `${t("checkout.validationSchema.clubName.regex")}`
            ),
    });

    const billingSchema = z.object({
        name: z
            .string()
            .min(2, `${t("checkout.validationSchema.billing.name.min")}`)
            .max(50, `${t("checkout.validationSchema.billing.name.max")}`)
            .regex(
                /^[a-zA-ZÀ-ÿ\s\-']+$/,
                `${t("checkout.validationSchema.billing.name.regex")}`
            ),
        surname: z
            .string()
            .min(2, `${t("checkout.validationSchema.billing.last_name.min")}`)
            .max(50, `${t("checkout.validationSchema.billing.last_name.max")}`)
            .regex(
                /^[a-zA-ZÀ-ÿ\s\-']+$/,
                `${t("checkout.validationSchema.billing.last_name.regex")}`
            ),
        mail: z
            .string()
            .email(`${t("checkout.validationSchema.billing.mail.email")}`)
            .max(100, `${t("checkout.validationSchema.billing.mail.max")}`),
        street_address: z
            .string()
            .min(5, `${t("checkout.validationSchema.billing.street_address.min")}`)
            .max(200, `${t("checkout.validationSchema.billing.street_address.max")}`),
        apartment: z
            .string()
            .max(50, `${t("checkout.validationSchema.billing.apartment.max")}`)
            .optional(),
        country_code: z
            .string()
            .min(2, `${t("checkout.validationSchema.billing.countryCode.min")}`)
            .max(3, `${t("checkout.validationSchema.billing.countryCode.invalid")}`),
        city: z
            .string()
            .min(2, `${t("checkout.validationSchema.billing.city.min")}`)
            .max(100, `${t("checkout.validationSchema.billing.city.max")}`)
            .regex(
                /^[a-zA-ZÀ-ÿ\s\-']+$/,
                `${t("checkout.validationSchema.billing.city.regex")}`
            ),
        zip_code: z
            .string()
            .min(3, `${t("checkout.validationSchema.billing.zipCode.min")}`)
            .max(20, `${t("checkout.validationSchema.billing.zipCode.max")}`)
            .regex(
                /^[a-zA-Z0-9\s\-]+$/,
                `${t("checkout.validationSchema.billing.zipCode.regex")}`
            ),
    });

    const fetchCountries = async () => {
        try {
            setCountriesLoading(true);
            const response = await fetch(
                `${BASE_URL}countries?language_code=${i18next.language.startsWith("en") ? "EN" : "ES"
                }`
            );
            const data = await response.json();
            if (data.status === "ok") {
                const validCountries = data.result.filter((country: Country) =>
                    i18next.language.startsWith("en") ? country.name_en : country.name
                );
                setCountries(validCountries);
            }
        } catch (error) {
            console.error("Error fetching countries:", error);
        } finally {
            setCountriesLoading(false);
        }
    };
    useEffect(() => {
        fetchCountries();
    }, [i18next.language]);

    // Legal agreements handlers
    const handleAgreementChange = (agreement: keyof typeof agreements) => {
        setAgreements(prev => ({
            ...prev,
            [agreement]: !prev[agreement]
        }));

        // Clear error when toggled
        if (agreementErrors[agreement]) {
            setAgreementErrors(prev => ({
                ...prev,
                [agreement]: ""
            }));
        }
    };

    const validateAgreements = () => {
        const errors: ValidationErrors = {};

        if (!agreements.dpa) {
            errors.dpa = t("agreementRequired");
        }

        if (!agreements.terms) {
            errors.terms = t("agreementRequired");
        }

        if (!isContractSigned) {
            errors.contract = t("agreementRequired");
        }

        setAgreementErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const openModal = (content: string) => {
        setModalContent(content);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setModalContent("");
    };

    // Real-time validation functions
    const validateClubField = (field: string, value: string) => {
        try {
            clubSchema.shape[field as keyof typeof clubSchema.shape].parse(value);
            setClubErrors((prev) => ({ ...prev, [field]: "" }));
            return true;
        } catch (error) {
            if (error instanceof z.ZodError) {
                setClubErrors((prev) => ({
                    ...prev,
                    //@ts-ignore
                    [field]: error.errors[0].message,
                }));
            }
            return false;
        }
    };
    const handleSignContract = () => {
        // Marca el contrato como firmado
        setIsContractSigned(true);
        closeModal();
    };

    const clearSignature = () => {
        signatureRef.current?.clear?.();
    };
    const validateBillingField = (field: string, value: string) => {
        try {
            billingSchema.shape[field as keyof typeof billingSchema.shape].parse(
                value
            );
            setBillingErrors((prev) => ({ ...prev, [field]: "" }));
            return true;
        } catch (error) {
            if (error instanceof z.ZodError) {
                setBillingErrors((prev) => ({
                    ...prev, //@ts-ignore
                    [field]: error.errors[0].message,
                }));
            }
            return false;
        }
    };

    const handleChangeClub = (type: string, val: string) => {
        setFormValuesClub((prev: any) => ({ ...prev, [type]: val }));

        // Validate if field has been touched
        if (touchedFields[type]) {
            validateClubField(type, val);
        }
    };

    const handleChangeBilling = (type: string, val: string) => {
        setFormValuesBilling((prev: any) => ({ ...prev, [type]: val }));

        // Validate if field has been touched
        if (touchedFields[type]) {
            validateBillingField(type, val);
        }
    };

    const handleFieldBlur = (
        field: string,
        value: string,
        type: "club" | "billing"
    ) => {
        setTouchedFields((prev) => ({ ...prev, [field]: true }));

        if (type === "club") {
            validateClubField(field, value);
        } else {
            validateBillingField(field, value);
        }
    };

    const handleCountrySelect = (country: Country) => {
        handleChangeBilling("country_code", country.country_code);
        setSearchTerm(country.name_en);
        setIsDropdownVisible(false);
        setNationalityCodes(country.country_code);

        // Clear country error when selected
        setBillingErrors((prev) => ({ ...prev, country_code: "" }));
    };

    const validateAllFields = () => {
        // Validate club data
        const clubValidation = clubSchema.safeParse(formValuesClub);
        if (!clubValidation.success) {
            const errors: ValidationErrors = {};
            clubValidation.error.errors.forEach((error) => {
                errors[error.path[0] as string] = error.message;
            });
            setClubErrors(errors);
        } else {
            setClubErrors({});
        }

        // Validate billing data
        const billingValidation = billingSchema.safeParse(formValuesBilling);
        if (!billingValidation.success) {
            const errors: ValidationErrors = {};
            billingValidation.error.errors.forEach((error) => {
                errors[error.path[0] as string] = error.message;
            });
            setBillingErrors(errors);
        } else {
            setBillingErrors({});
        }

        // Validate agreements
        const agreementsValid = validateAgreements();

        return clubValidation.success && billingValidation.success && agreementsValid;
    };

    const handleContinue = async () => {
        // Mark all fields as touched
        const allFields = [
            ...Object.keys(formValuesClub),
            ...Object.keys(formValuesBilling),
        ];
        const touchedState = allFields.reduce(
            (acc, field) => ({
                ...acc,
                [field]: true,
            }),
            {}
        );
        setTouchedFields(touchedState);

        if (!validateAllFields()) {
            // Scroll to first error
            const firstError = document.querySelector(".error-field");
            if (firstError) {
                firstError.scrollIntoView({ behavior: "smooth", block: "center" });
            }

            // If agreements have errors, scroll to them
            if (Object.keys(agreementErrors).length > 0) {
                const agreementError = document.querySelector(".agreement-error");
                if (agreementError) {
                    agreementError.scrollIntoView({ behavior: "smooth", block: "center" });
                }
            }

            return;
        }

        setIsLoading(true);

        try {
            const subscriptionPriceId = (() => {
                switch (items.subscriptions.price) {
                    case 30:
                        return "price_1TMoQu7plh7U2nikgDgHWKdT";
                    case 60:
                        return "price_1TMnez7plh7U2nikGcZgrrh9";
                    case 90:
                        return "price_1TMoRg7plh7U2nikgessp3tK";
                    default:
                        return "price_1TMnez7plh7U2nikGcZgrrh9";
                }
            })();

            // Store items before payment
            setItems((prev: any) => ({
                ...prev,
                clubDetails: {
                    ...formValuesClub,
                    clubCategory: categoryID,
                    clubDivision: divisionID,
                    clubCountry: nationalitieCodes,
                },
                deliveryBillingDetails: formValuesBilling,
            }));

            // Make payment request
            const response = await fetch("https://wizardpayments.francecentral.cloudapp.azure.com/wizard/wizard-cam-subscription", {
                headers: {
                    "Content-Type": "application/json"
                },
                method: "POST",
                body: JSON.stringify({
                    ...items,
                    subscriptions: subscriptionPriceId,
                    clubDetails: {
                        ...formValuesClub,
                        clubCategory: categoryID,
                        clubDivision: divisionID,
                        clubCountry: nationalitieCodes,
                    },
                    deliveryBillingDetails: formValuesBilling,
                    agreements: agreements // Include agreements in the request
                })
            }).then((res) => res.json()).then((data) => {
                if (data.sessionId) {
                    // Store agreements acceptance
                    localStorage.setItem('agreements_accepted', JSON.stringify({
                        dpa: agreements.dpa,
                        terms: agreements.terms,
                        contract: isContractSigned,
                        acceptedAt: new Date().toISOString()
                    }));

                    // Redirect to payment
                    window.location.href = data.redirectUrl;
                }
            }).catch((err) => {
                console.error(err);
                // Handle error - maybe show toast
            });

            console.log(response);
            // await new Promise((resolve) => setTimeout(resolve, 1000));
            // setStep(step + 1);
        } catch (error) {
            console.error("Error saving data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const getInputClassName = (fieldName: string, errors: ValidationErrors) => {
        const hasError = errors[fieldName] && touchedFields[fieldName];
        const isValid =
            touchedFields[fieldName] &&
            !errors[fieldName] &&
            (fieldName === "clubName"
                ? formValuesClub[fieldName]
                : //@ts-ignore
                formValuesBilling[fieldName]);

        return `w-full px-4 py-3 rounded-lg border transition-all duration-200 ${hasError
            ? "border-red-500 bg-red-50 focus:ring-2 focus:ring-red-500 focus:border-red-500 error-field"
            : isValid
                ? "border-green-500 bg-green-50 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                : "border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-400"
            }`;
    };

    const renderFieldError = (fieldName: string, errors: ValidationErrors) => {
        if (errors[fieldName] && touchedFields[fieldName]) {
            return (
                <div className="flex items-center mt-1 text-red-600 text-sm">
                    <GoAlert className="w-4 h-4 mr-1 flex-shrink-0" />
                    <span>{errors[fieldName]}</span>
                </div>
            );
        }
        return null;
    };

    const hasErrors =
        Object.values(clubErrors).some((error) => error) ||
        Object.values(billingErrors).some((error) => error) ||
        Object.values(agreementErrors).some((error) => error);

    const isFormComplete =
        formValuesClub.clubName &&
        formValuesBilling.name &&
        formValuesBilling.surname &&
        formValuesBilling.mail &&
        formValuesBilling.street_address &&
        formValuesBilling.country_code &&
        formValuesBilling.city &&
        formValuesBilling.zip_code &&
        agreements.dpa &&
        agreements.terms &&
        isContractSigned

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
            <div className="h-full wiz-custom-scroll-bar p-6 lg:p-14 lg:w-3/4">
                <div className="max-w-4xl mx-auto">
                    {/* Club Creation Section */}
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mb-8">
                        <div className="mb-8">
                            <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-3">
                                {t("clubCreation.title")}
                            </h1>
                            <p className="text-lg text-gray-600 leading-relaxed">
                                {t("clubCreation.description")}
                            </p>
                        </div>

                        <div className="space-y-6">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    {t("clubCreation.clubNameLabel")}{" "}
                                    <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="clubName"
                                    value={formValuesClub.clubName}
                                    onChange={(e) => handleChangeClub("clubName", e.target.value)}
                                    onBlur={(e) =>
                                        handleFieldBlur("clubName", e.target.value, "club")
                                    }
                                    placeholder={t("clubCreation.clubNamePlaceholder")}
                                    className={getInputClassName("clubName", clubErrors)}
                                    autoComplete="organization"
                                />
                                {renderFieldError("clubName", clubErrors)}
                            </div>
                        </div>
                    </div>

                    {/* Billing Information Section */}
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mb-8">
                        <div className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                {t("clubCreation.deliveryBillingTitle")}
                            </h2>
                            <p className="text-gray-600">
                                {t("clubCreation.deliveryBillingSubtitle")}
                            </p>
                        </div>

                        {/* Personal Information */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    {t("clubCreation.billing.nameLabel")}{" "}
                                    <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder={t("clubCreation.billing.namePlaceholder")}
                                    value={formValuesBilling.name}
                                    onChange={(e) => handleChangeBilling("name", e.target.value)}
                                    onBlur={(e) =>
                                        handleFieldBlur("name", e.target.value, "billing")
                                    }
                                    className={getInputClassName("name", billingErrors)}
                                    autoComplete="given-name"
                                />
                                {renderFieldError("name", billingErrors)}
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    {t("clubCreation.billing.lastnameLabel")}{" "}
                                    <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder={t("clubCreation.billing.lastnamePlaceholder")}
                                    value={formValuesBilling.surname}
                                    onChange={(e) =>
                                        handleChangeBilling("surname", e.target.value)
                                    }
                                    onBlur={(e) =>
                                        handleFieldBlur("surname", e.target.value, "billing")
                                    }
                                    className={getInputClassName("surname", billingErrors)}
                                    autoComplete="family-name"
                                />
                                {renderFieldError("surname", billingErrors)}
                            </div>
                        </div>

                        {/* Email */}
                        <div className="mb-6">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                {t("clubCreation.billing.emailLabel")}{" "}
                                <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="email"
                                placeholder={t("clubCreation.billing.emailPlaceholder")}
                                value={formValuesBilling.mail}
                                onChange={(e) => handleChangeBilling("mail", e.target.value)}
                                onBlur={(e) =>
                                    handleFieldBlur("mail", e.target.value, "billing")
                                }
                                className={getInputClassName("mail", billingErrors)}
                                autoComplete="email"
                            />
                            {renderFieldError("mail", billingErrors)}
                        </div>

                        {/* Address */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                            <div className="md:col-span-2">
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    {t("clubCreation.billing.streetLabel")}{" "}
                                    <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder={t("clubCreation.billing.streetPlaceholder")}
                                    value={formValuesBilling.street_address}
                                    onChange={(e) =>
                                        handleChangeBilling("street_address", e.target.value)
                                    }
                                    onBlur={(e) =>
                                        handleFieldBlur("street_address", e.target.value, "billing")
                                    }
                                    className={getInputClassName("street_address", billingErrors)}
                                    autoComplete="street-address"
                                />
                                {renderFieldError("street_address", billingErrors)}
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    {t("clubCreation.billing.apartmentLabel")}
                                </label>
                                <input
                                    type="text"
                                    placeholder={t("clubCreation.billing.apartmentPlaceholder")}
                                    value={formValuesBilling.apartment}
                                    onChange={(e) =>
                                        handleChangeBilling("apartment", e.target.value)
                                    }
                                    onBlur={(e) =>
                                        handleFieldBlur("apartment", e.target.value, "billing")
                                    }
                                    className={getInputClassName("apartment", billingErrors)}
                                    autoComplete="address-line2"
                                />
                                {renderFieldError("apartment", billingErrors)}
                            </div>
                        </div>

                        {/* Country Dropdown */}
                        <div className="mb-6">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                {t("clubCreation.billing.countryLabel")}{" "}
                                <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <select
                                    name="country_code"
                                    value={formValuesBilling.country_code || ""}
                                    onChange={(e) => {
                                        const selectedCountry = countries.find(
                                            (c) => c.country_code === e.target.value
                                        );
                                        if (selectedCountry) {
                                            handleCountrySelect(selectedCountry);
                                        }
                                    }}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                    required
                                >
                                    <option value="">{t("clubCreation.billing.countrySelectPlaceholder")}</option>
                                    {countries.map((country: Country) => (
                                        <option
                                            key={country.country_code}
                                            value={country.country_code}
                                        >
                                            {i18next.language === "en"
                                                ? country.name_en
                                                : country.name}
                                        </option>
                                    ))}
                                </select>

                                {/* Custom select display */}
                                <div
                                    className={`
                                        w-full px-4 py-3 pr-10 rounded-lg border
                                        ${billingErrors.country_code ? "border-red-500" : "border-gray-300"}
                                        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                                        flex items-center bg-white cursor-pointer
                                    `}
                                    onClick={() => setIsDropdownVisible(!isDropdownVisible)}
                                    onBlur={() =>
                                        setTimeout(() => setIsDropdownVisible(false), 200)
                                    }
                                    tabIndex={0}
                                >
                                    {formValuesBilling.country_code ? (
                                        <>
                                            <img
                                                src={`${BASE_URL_IMG}${countries.find(
                                                    (c) =>
                                                        c.country_code === formValuesBilling.country_code
                                                )?.country_flag
                                                    }`}
                                                alt=""
                                                className="w-6 h-4 mr-3 rounded-sm object-cover"
                                            />
                                            <span className="truncate">
                                                {i18next.language === "en"
                                                    ? countries.find(
                                                        (c) =>
                                                            c.country_code ===
                                                            formValuesBilling.country_code
                                                    )?.name_en
                                                    : countries.find(
                                                        (c) =>
                                                            c.country_code ===
                                                            formValuesBilling.country_code
                                                    )?.name}
                                            </span>
                                        </>
                                    ) : (
                                        <span className="text-gray-400">
                                            {countriesLoading
                                                ? t("clubCreation.billing.loadingCountries")
                                                : t("clubCreation.billing.countryPlaceholder")}
                                        </span>
                                    )}
                                    <svg
                                        className={`w-5 h-5 text-gray-400 ml-auto transform transition-transform ${isDropdownVisible ? "rotate-180" : ""
                                            }`}
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </div>

                                {/* Dropdown menu */}
                                {isDropdownVisible && (
                                    <div className="absolute z-20 mt-1 w-full bg-white shadow-xl rounded-lg border border-gray-200 max-h-60 overflow-y-auto">
                                        {countriesLoading ? (
                                            <div className="px-4 py-3 text-gray-500">
                                                {t("clubCreation.billing.loadingCountries")}
                                            </div>
                                        ) : (
                                            countries
                                                .filter((country: Country) =>
                                                    searchTerm
                                                        ? i18next.language === "en-US"
                                                            ? country.name_en
                                                                ?.toLowerCase()
                                                                .includes(searchTerm.toLowerCase())
                                                            : country.name
                                                                ?.toLowerCase()
                                                                .includes(searchTerm.toLowerCase())
                                                        : true
                                                )
                                                .map((country: Country) => (
                                                    <div
                                                        key={country.country_code}
                                                        className="flex items-center px-4 py-3 hover:bg-blue-50 cursor-pointer transition-colors border-b border-gray-50 last:border-b-0"
                                                        onClick={() => {
                                                            handleCountrySelect(country);
                                                            setIsDropdownVisible(false);
                                                            setSearchTerm("");
                                                        }}
                                                    >
                                                        <img
                                                            src={`${BASE_URL_IMG}${country.country_flag}`}
                                                            alt=""
                                                            className="w-6 h-4 mr-3 rounded-sm object-cover"
                                                        />
                                                        <span className="text-gray-900">
                                                            {i18next.language === "en-US"
                                                                ? country.name_en
                                                                : country.name}
                                                        </span>
                                                    </div>
                                                ))
                                        )}
                                    </div>
                                )}
                            </div>
                            {renderFieldError("country_code", billingErrors)}
                        </div>

                        {/* City & Zip */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    {t("clubCreation.billing.cityLabel")}{" "}
                                    <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder={t("clubCreation.billing.cityPlaceholder")}
                                    value={formValuesBilling.city}
                                    onChange={(e) => handleChangeBilling("city", e.target.value)}
                                    onBlur={(e) =>
                                        handleFieldBlur("city", e.target.value, "billing")
                                    }
                                    className={getInputClassName("city", billingErrors)}
                                    autoComplete="address-level2"
                                />
                                {renderFieldError("city", billingErrors)}
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    {t("clubCreation.billing.zipLabel")}{" "}
                                    <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder={t("clubCreation.billing.zipPlaceholder")}
                                    value={formValuesBilling.zip_code}
                                    onChange={(e) =>
                                        handleChangeBilling("zip_code", e.target.value)
                                    }
                                    onBlur={(e) =>
                                        handleFieldBlur("zip_code", e.target.value, "billing")
                                    }
                                    className={getInputClassName("zip_code", billingErrors)}
                                    autoComplete="postal-code"
                                />
                                {renderFieldError("zip_code", billingErrors)}
                            </div>
                        </div>

                        {/* Shipping Method */}
                        <div className="mb-8">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                {t("clubCreation.shipping.methodTitle")}
                            </h3>
                            <div className="border-2 border-blue-500 bg-blue-50 rounded-xl p-4 transition-all">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-4">
                                        <div className="w-5 h-5 rounded-full border-2 border-blue-500 flex items-center justify-center">
                                            <div className="w-2.5 h-2.5 rounded-full bg-blue-500"></div>
                                        </div>
                                        <span className="font-medium text-gray-900">
                                            {t("clubCreation.shipping.standard")}
                                        </span>
                                    </div>
                                    <span className="text-gray-600 font-medium">
                                        {t("clubCreation.shipping.estimate")}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Legal Agreements Section */}
                        <div className="mt-8 pt-8 border-t border-gray-200 agreement-error">
                            <h3 className="text-lg font-semibold text-gray-900 mb-6">
                                {t("legalAgreements.title")}
                            </h3>

                            <div className="space-y-6">
                                {/* DPA Agreement */}
                                <div className={`flex gap-3 items-center p-4 rounded-lg transition-colors ${agreementErrors.dpa ? 'bg-red-50 border border-red-200' : 'bg-gray-50 hover:bg-gray-100'}`}>
                                    <button
                                        onClick={() => handleAgreementChange('dpa')}
                                        className={`mt-0.5 w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${agreements.dpa
                                            ? 'bg-blue-600 border-blue-600'
                                            : 'border-gray-300 hover:border-blue-400'
                                            }`}
                                    >
                                        {agreements.dpa && (
                                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                            </svg>
                                        )}
                                    </button>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            {/* <Shield className="w-4 h-4 text-blue-600" /> */}
                                            <p className="text-sm text-gray-700">
                                                {t("dpaAgreement")}{" "}
                                                <button
                                                    type="button"
                                                    onClick={() => openModal(dpa)}
                                                    className="text-blue-600 hover:text-blue-800 underline font-medium"
                                                >
                                                    {t("dpaAgreement_2")}
                                                </button>{" "}
                                                {t("dpaAgreement_3")}
                                            </p>
                                        </div>
                                        {agreementErrors.dpa && (
                                            <p className="text-red-600 text-xs mt-1 flex items-center gap-1">
                                                <GoAlert className="w-3 h-3" />
                                                {agreementErrors.dpa}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Terms & Privacy Agreement */}
                                <div className={`flex gap-3 items-center p-4 rounded-lg transition-colors ${agreementErrors.terms ? 'bg-red-50 border border-red-200' : 'bg-gray-50 hover:bg-gray-100'}`}>
                                    <button
                                        onClick={() => handleAgreementChange('terms')}
                                        className={`mt-0.5 w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${agreements.terms
                                            ? 'bg-blue-600 border-blue-600'
                                            : 'border-gray-300 hover:border-blue-400'
                                            }`}
                                    >
                                        {agreements.terms && (
                                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                            </svg>
                                        )}
                                    </button>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            {/* <FileText className="w-4 h-4 text-blue-600" /> */}
                                            <p className="text-sm text-gray-700">
                                                {t("termsAgreement")}{" "}
                                                <button
                                                    type="button"
                                                    onClick={() => openModal(terms)}
                                                    className="text-blue-600 hover:text-blue-800 underline font-medium"
                                                >
                                                    {t("termsAgreement_2")}
                                                </button>{" "}
                                                {t("termsAgreement_3")}{" "}
                                                <button
                                                    type="button"
                                                    onClick={() => openModal(privacy)}
                                                    className="text-blue-600 hover:text-blue-800 underline font-medium"
                                                >
                                                    {t("termsAgreement_4")}
                                                </button>
                                            </p>
                                        </div>
                                        {agreementErrors.terms && (
                                            <p className="text-red-600 text-xs mt-1 flex items-center gap-1">
                                                <GoAlert className="w-3 h-3" />
                                                {agreementErrors.terms}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Contract Agreement */}
                                <div className={`flex gap-3 items-center p-4 rounded-lg transition-colors ${agreementErrors.contract ? 'bg-red-50 border border-red-200' : 'bg-gray-50 hover:bg-gray-100'}`}>

                                    <div className="flex-1">
                                        <div className="flex gap-3 items-start">
                                            <input
                                                type="checkbox"
                                                className="mt-1 w-5 h-5"
                                                disabled={!isContractSigned}
                                                checked={isContractSigned}
                                            />
                                            <p className="text-sm text-gray-700">
                                                {t("contractAgreement")}{" "}
                                                <button
                                                    onClick={() => openModal(contract)}
                                                    className="text-blue-600 underline"
                                                >
                                                    {t("contractAgreement_2")}
                                                </button>
                                            </p>
                                        </div>
                                        {agreementErrors.contract && (
                                            <p className="text-red-600 text-xs mt-1 flex items-center gap-1">
                                                <GoAlert className="w-3 h-3" />
                                                {agreementErrors.contract}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Required Fields Note */}
                            <div className="mt-6 pt-4 border-t border-gray-200">
                                <p className="text-xs text-gray-500">
                                    <span className="text-red-500">*</span> {t("legalAgreements.requiredNote")}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Navigation Buttons */}
                    <div className="flex justify-between items-center pt-6 border-t border-gray-200 pb-40">
                        <button
                            onClick={() => setStep(step - 1)}
                            className="px-8 py-3 border-2 border-gray-300 rounded-full font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200"
                        >
                            {t("clubCreation.buttons.back")}
                        </button>

                        <button
                            onClick={handleContinue}
                            disabled={isLoading || !isFormComplete}
                            className={`px-8 py-3 rounded-full font-semibold transition-all duration-200 flex items-center space-x-2 ${isLoading || !isFormComplete
                                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                : "bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 transform hover:scale-105 shadow-lg hover:shadow-xl"
                                }`}
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="h-5 w-5 animate-spin" />
                                    <span>{t("checkout.buttons.processing")}</span>
                                </>
                            ) : (
                                <>
                                    <span>{t("clubCreation.buttons.continue")}</span>
                                    <GoArrowRight className="h-5 w-5" />
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Modal for viewing documents */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
                    <div className="bg-white rounded-lg overflow-hidden shadow-xl w-[90%] max-w-4xl relative">
                        <button
                            onClick={closeModal}
                            className="absolute top-4 right-4 z-20 rounded-full bg-white/90 p-2 text-gray-500 shadow-md hover:text-gray-700"
                        >
                            ✖
                        </button>
                        <iframe src={modalContent} className="w-full h-[70vh] mt-14" />
                        {modalContent === contract && (
                            <div className="p-6 space-y-4">
                                <p className="font-medium">{t("customerSignature")}</p>
                                <SignatureCanvas
                                    ref={signatureRef}
                                    penColor="black"
                                    canvasProps={{
                                        width: 500,
                                        height: 100,
                                        className: "border border-gray-400 rounded",
                                    }}
                                />
                                <div className="flex flex-col sm:flex-row justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={clearSignature}
                                        className="px-6 py-2 text-sm rounded-full border border-gray-300 text-gray-700 hover:bg-gray-50 transition"
                                    >
                                        {t("clearSignature") || "Rehacer firma"}
                                    </button>
                                    <button
                                        onClick={handleSignContract}
                                        className="px-6 py-2 text-sm rounded-full bg-blue-700 text-white hover:bg-blue-800 transition"
                                    >
                                        {t("signButton")}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClubCreationStep;
